

import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { useResponsiveValue } from '@/hooks/use-responsive-value';
import { organizationCoreSchema } from '@/lib/schemas/organization';

export function BasicIdentitySection({ isNameReadonly }: { isNameReadonly?: boolean }) {
  const {
    register,
    watch,
    formState: { errors },
  } = useFormContext<z.infer<typeof organizationCoreSchema>>();
  const resolveResponsive = useResponsiveValue();
  const { t } = useTranslation();

  return (
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 700, mb: 0.5 }}>
        {t('entity_form.organizations.labels.main_info_title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        {t('entity_form.organizations.labels.main_info_desc')}
      </Typography>

      <Grid container spacing={3}>
        <Grid size={resolveResponsive({ xs: 12, md: 6 })}>
          <TextField
            {...register('name')}
            label={t('entity_form.organizations.labels.name')}
            placeholder={t('entity_form.organizations.labels.name_placeholder')}
            required
            fullWidth
            variant="standard"
            error={!!errors.name}
            helperText={errors.name?.message}
            InputLabelProps={{ shrink: true }}
            disabled={isNameReadonly}
            sx={{
              '& .MuiInput-root': {
                height: 40,
                fontSize: '0.9rem',
                '&:before': { borderBottomWidth: 1 },
                '&:after': { borderBottomWidth: 2 }
              }
            }}
          />
        </Grid>

        <Grid size={resolveResponsive({ xs: 12, md: 6 })}>
          <TextField
            {...register('type')}
            label={t('entity_form.organizations.labels.type')}
            placeholder={t('entity_form.organizations.labels.type_placeholder')}
            required
            fullWidth
            variant="standard"
            error={!!errors.type}
            helperText={errors.type?.message}
            InputLabelProps={{ shrink: true }}
            sx={{
              '& .MuiInput-root': {
                height: 40,
                fontSize: '0.9rem',
                '&:before': { borderBottomWidth: 1 },
                '&:after': { borderBottomWidth: 2 }
              }
            }}
          />
        </Grid>

        <Grid size={resolveResponsive({ xs: 12, md: 4 })}>
          <TextField
            {...register('foundingDate')}
            label={t('entity_form.organizations.labels.founding_date')}
            placeholder={t('entity_form.organizations.labels.founding_date_placeholder')}
            fullWidth
            variant="filled"
            size="small"
            error={!!errors.foundingDate}
            helperText={errors.foundingDate?.message}
            InputProps={{ disableUnderline: true, sx: { borderRadius: 1.5, px: 1.5 } }}
            InputLabelProps={{ shrink: true }}
            sx={{
              '& .MuiFilledInput-root': {
                height: 44,
              }
            }}
          />
        </Grid>

        <Grid size={resolveResponsive({ xs: 12, md: 4 })}>
          <TextField
            {...register('currentLeaders')}
            label={t('entity_form.organizations.labels.current_leaders')}
            placeholder={t('entity_form.organizations.labels.current_leaders_placeholder')}
            fullWidth
            variant="filled"
            size="small"
            error={!!errors.currentLeaders}
            helperText={errors.currentLeaders?.message}
            InputProps={{ disableUnderline: true, sx: { borderRadius: 1.5, px: 1.5 } }}
            InputLabelProps={{ shrink: true }}
            sx={{
              '& .MuiFilledInput-root': {
                height: 44,
              }
            }}
          />
        </Grid>

        <Grid size={resolveResponsive({ xs: 12, md: 4 })}>
          <TextField
            {...register('headquarters')}
            label={t('entity_form.organizations.labels.headquarters')}
            placeholder={t('entity_form.organizations.labels.headquarters_placeholder')}
            fullWidth
            variant="filled"
            size="small"
            error={!!errors.headquarters}
            helperText={errors.headquarters?.message}
            InputProps={{ disableUnderline: true, sx: { borderRadius: 1.5, px: 1.5 } }}
            InputLabelProps={{ shrink: true }}
            sx={{
              '& .MuiFilledInput-root': {
                height: 44,
              }
            }}
          />
        </Grid>

        <Grid size={resolveResponsive({ xs: 12, md: 6 })}>
          <TextField
            {...register('alignment')}
            label={t('entity_form.organizations.labels.alignment')}
            placeholder={t('entity_form.organizations.labels.alignment_placeholder')}
            fullWidth
            variant="filled"
            size="small"
            error={!!errors.alignment}
            helperText={errors.alignment?.message}
            InputProps={{ disableUnderline: true, sx: { borderRadius: 1.5, px: 1.5 } }}
            InputLabelProps={{ shrink: true }}
            sx={{
              '& .MuiFilledInput-root': {
                height: 44,
              }
            }}
          />
        </Grid>

        <Grid size={resolveResponsive({ xs: 12, md: 6 })}>
          <TextField
            {...register('reputation')}
            label={t('entity_form.organizations.labels.reputation')}
            placeholder={t('entity_form.organizations.labels.reputation_placeholder')}
            fullWidth
            variant="filled"
            size="small"
            error={!!errors.reputation}
            helperText={errors.reputation?.message}
            InputProps={{ disableUnderline: true, sx: { borderRadius: 1.5, px: 1.5 } }}
            InputLabelProps={{ shrink: true }}
            sx={{
              '& .MuiFilledInput-root': {
                height: 44,
              }
            }}
          />
        </Grid>

        <Grid size={resolveResponsive({ xs: 12 })}>
          <TextField
            {...register('shortDescription')}
            label={t('entity_form.organizations.labels.short_desc')}
            placeholder={t('entity_form.organizations.labels.short_desc_placeholder')}
            required
            fullWidth
            multiline
            minRows={2}
            variant="filled"
            size="small"
            error={!!errors.shortDescription}
            helperText={errors.shortDescription?.message}
            InputProps={{
              disableUnderline: true,
              sx: {
                borderRadius: 1.5,
                p: 1.5,
                '& textarea': { resize: 'vertical' }
              }
            }}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid size={resolveResponsive({ xs: 12 })}>
          <TextField
            {...register('visualDescription')}
            label={t('entity_form.organizations.labels.visual_description')}
            placeholder={t('entity_form.organizations.labels.visual_description_placeholder')}
            fullWidth
            multiline
            minRows={3}
            variant="filled"
            size="small"
            inputProps={{ maxLength: 1500 }}
            error={!!errors.visualDescription}
            helperText={errors.visualDescription?.message || `${(watch('visualDescription') || '').length} / 1500`}
            InputProps={{
              disableUnderline: true,
              sx: {
                borderRadius: 1.5,
                p: 1.5,
                '& textarea': { resize: 'vertical' }
              }
            }}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
      </Grid>
    </Paper>
  );
}
